import { Pressable, ScrollView, View } from 'react-native';
import type { ScreenSize } from '../screenSize';
import { colors, HIT, radii, SIDE_NAV_WIDTH } from '../tokens';
import { Icon, type IconName } from './Icon';
import { LogoMark, LogoWordmark } from './logo';
import { Text } from './primitives';

export type NavItem = { key: string; label: string; icon: IconName; onPress: () => void };

export type NavProps = { items: NavItem[]; active: string; label: string };

export const BottomNav = ({ items, active, label }: NavProps) => (
  <View accessibilityRole="navigation" aria-label={label} style={{ height: 78, backgroundColor: colors.surface, borderTopWidth: 1, borderTopColor: colors.border, flexDirection: 'row', paddingHorizontal: 6, paddingBottom: 12 }}>
    {items.map((item) => {
      const on = item.key === active;
      const color = on ? colors.primaryText : colors.textMuted;
      return (
        <Pressable
          key={item.key}
          accessibilityRole="link"
          accessibilityState={{ selected: on }}
          onPress={item.onPress}
          style={{ flex: 1, alignItems: 'center', justifyContent: 'center', gap: 3 }}
        >
          <Icon name={item.icon} size={24} color={color} strokeWidth={on ? 2.2 : 2} />
          <Text variant="label" color={color} style={{ fontWeight: on ? '700' : '600' }}>
            {item.label}
          </Text>
        </Pressable>
      );
    })}
  </View>
);

/** Selected item on the sky-blue wide navs (the wordmark is white, so the bars use the logo background). */
const WIDE_ACTIVE_BG = 'rgba(255,255,255,.6)';

/** Header bar for wide screens; items scroll sideways rather than wrap if a tablet is too narrow for them. */
export const TopNav = ({ items, active, label, size }: NavProps & { size: ScreenSize }) => (
  <View style={{ height: 64, backgroundColor: colors.sky, flexDirection: 'row', alignItems: 'center', gap: 16, paddingHorizontal: 16 }}>
    {size === 'desktop' ? <LogoWordmark height={30} /> : <LogoMark size={36} />}
    <ScrollView horizontal showsHorizontalScrollIndicator={false} style={{ flex: 1 }} contentContainerStyle={{ flexGrow: 1, justifyContent: 'flex-end' }}>
      <View accessibilityRole="navigation" aria-label={label} style={{ flexDirection: 'row', alignItems: 'center', gap: 4 }}>
        {items.map((item) => {
          const on = item.key === active;
          const color = colors.text;
          return (
            <Pressable
              key={item.key}
              accessibilityRole="link"
              accessibilityState={{ selected: on }}
              onPress={item.onPress}
              style={({ pressed }) => ({
                height: HIT,
                paddingHorizontal: 12,
                borderRadius: radii.pill,
                flexDirection: 'row',
                alignItems: 'center',
                gap: 6,
                backgroundColor: on ? WIDE_ACTIVE_BG : 'transparent',
                transform: [{ scale: pressed ? 0.97 : 1 }],
              })}
            >
              <Icon name={item.icon} size={20} color={color} strokeWidth={on ? 2.2 : 2} />
              <Text variant="bodyStrong" color={color} style={{ fontSize: 14, fontWeight: on ? '700' : '600' }} numberOfLines={1}>
                {item.label}
              </Text>
            </Pressable>
          );
        })}
      </View>
    </ScrollView>
  </View>
);

/** Left column for wide screens: labelled on desktop, a 72px icon rail on tablets (labels move to aria-label). */
export const SideNav = ({ items, active, label, size }: NavProps & { size: ScreenSize }) => {
  const rail = size !== 'desktop';
  return (
    <View
      style={{
        width: rail ? SIDE_NAV_WIDTH.tablet : SIDE_NAV_WIDTH.desktop,
        backgroundColor: colors.sky,
        paddingVertical: 16,
        paddingHorizontal: rail ? 10 : 14,
        gap: 20,
      }}
    >
      <View style={{ alignItems: rail ? 'center' : 'flex-start', paddingHorizontal: rail ? 0 : 6 }}>{rail ? <LogoMark size={40} /> : <LogoWordmark height={32} />}</View>
      <ScrollView style={{ flex: 1 }} contentContainerStyle={{ gap: 4 }}>
        <View accessibilityRole="navigation" aria-label={label} style={{ gap: 4 }}>
          {items.map((item) => {
            const on = item.key === active;
            const color = colors.text;
            return (
              <Pressable
                key={item.key}
                accessibilityRole="link"
                accessibilityState={{ selected: on }}
                accessibilityLabel={rail ? item.label : undefined}
                onPress={item.onPress}
                style={({ pressed }) => ({
                  height: 48,
                  borderRadius: radii.md,
                  flexDirection: 'row',
                  alignItems: 'center',
                  justifyContent: rail ? 'center' : 'flex-start',
                  gap: 12,
                  paddingHorizontal: rail ? 0 : 12,
                  backgroundColor: on ? WIDE_ACTIVE_BG : 'transparent',
                  transform: [{ scale: pressed ? 0.97 : 1 }],
                })}
              >
                <Icon name={item.icon} size={22} color={color} strokeWidth={on ? 2.2 : 2} />
                {rail ? null : (
                  <Text variant="bodyStrong" color={color} style={{ fontSize: 14, fontWeight: on ? '700' : '600' }} numberOfLines={1}>
                    {item.label}
                  </Text>
                )}
              </Pressable>
            );
          })}
        </View>
      </ScrollView>
    </View>
  );
};
